import { useState } from 'react';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { deserialize, serialize } from 'serializr';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import { OrganizationDataModel } from 'models/CompanySetup/organizationDataModel.model';
import { notification } from 'antd';
import { CreateLocationPayload } from 'models/CompanySetup/createLocationPayload.model';
import { Payload } from 'models/CompanySetup/organisationPayload.model';
import { ALLLocations } from 'models/CompanySetup/allLocations.model';
import { CreateLocationsPayload } from 'models/CompanySetup/createLocations.model';
import { useLocationsDataContext } from 'context/LocationsDataContext';
import { InfoDataModel } from 'models/MainDashboard/infoGraphics.model';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';
import { generatePath } from 'react-router-dom';
import { MetaData } from 'models/MetaData/metaData.model';

const ParentCompanySetupService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [infoData, setInfoData] = useState<InfoDataModel>();
  const [sectorMetaData, setSectorMetaData] = useState<MetaData[]>([]);
  const { setOrganizationData } = useParentOrganizationContext();
  const { setLocationTableData, clearLocationContext } = useLocationsDataContext();

  const fetchParentOrganization = async () => {
    try {
      setLoading(true);

      const response = await axiosInstance.get(ApiRoutes.PARENT_ORGANIZATION);

      const deserializedData = deserialize(
        OrganizationDataModel,
        response.data.data
      );

      setOrganizationData(deserializedData);
      return deserializedData;
    } catch (error) {
      setOrganizationData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllLocations = async (params: {
    year?: number;
    page?: number;
    search?: string;
    sort_by?: string;
    sort_dir?: string;
    organization_id?: string;
  }) => {
    try {
      setLoading(true);

      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(
          ([_, value]) => value !== undefined && value !== ''
        )
      );

      const response = await axiosInstance.get(
        ApiRoutes.CREATE_FIRST_LOCATION,
        {
          params: { ...filteredParams, limit: 10 },
        }
      );

      const deserializedData = deserialize(ALLLocations, response.data);
      setLocationTableData(deserializedData);
    } catch (error) {
      clearLocationContext();
    } finally {
      setLoading(false);
    }
  };

  const createParentOrganization = async (payload: Payload) => {
    try {
      setLoading(true);

      const response = await axiosInstance.post(
        ApiRoutes.CREATE_ORGANISATION,
        payload
      );

      const deserializedData = deserialize(
        OrganizationDataModel,
        response.data.data
      );

      setOrganizationData(deserializedData);
    } catch (error) {
      setOrganizationData(null);
    } finally {
      setLoading(false);
    }
  };

  const createFirstLocation = async (payload: CreateLocationPayload) => {
    try {
      setLoading(true);
      const serializedPayload = serialize(CreateLocationPayload, payload);

      const response = await axiosInstance.post(
        ApiRoutes.CREATE_FIRST_LOCATION,
        serializedPayload
      );
      if (response.data.data) {
        notification.success({
          message: 'Location Created Successfully',
        });
      }

      return response.data.data?.location.id;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchInfoGraphics = async (
    year: number,
    organizationId: number,
    locationId?: number
  ) => {
    let params = {
      year,
      organization_id: organizationId,
      location_id: locationId,
    };
    try {
      const response = await axiosInstance.get(ApiRoutes.INFO_GRAPHICS, {
        params,
      });
      const infoResponse = deserialize(InfoDataModel, response.data);

      setInfoData(infoResponse);
    } catch (error) { }
  };

  const createLocations = async (payload: CreateLocationsPayload) => {
    try {
      setLoading(true);
      const serializedPayload = serialize(CreateLocationsPayload, payload);
      const response = await axiosInstance.post(
        ApiRoutes.CREATE_FIRST_LOCATION,
        serializedPayload
      );
      if (response) {
        Notification({
          message: response?.data?.message ?? 'Locations Created Successfully',
          description: '',
          type: NotificationTypes.SUCCESS,
        });
      }

      return response.data;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchSector = async (companyTypesId: number) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.META_SECTOR, { id: companyTypesId });
      const response = await axiosInstance.get(url);
      setSectorMetaData(response.data.data.metadata);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    fetchParentOrganization,
    createParentOrganization,
    createFirstLocation,
    fetchAllLocations,
    createLocations,
    fetchInfoGraphics,
    infoData,
    fetchSector,
    sectorMetaData
  };
};

export default ParentCompanySetupService;
